import { Box, HStack, Link, Text, Tooltip } from "@chakra-ui/react";
import { RiBracesLine } from "@remixicon/react";
import type { ColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";

import CompactNumber from "@/common/components/CompactNumber";
import DataCell from "@/common/components/DataCell";
import { DataTable } from "@/common/components/DataTable/DataTable";
import { FilterGroupOnChange } from "@/common/components/filters/FilterGroup";
import NotAvailableCell from "@/common/components/NotAvailableCell";
import { featureMetricOptions } from "@/common/components/Rule/RuleMetric";
import TimestampCell from "@/common/components/TimestampCell";
import { UserDisplay } from "@/common/components/UserDisplay";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import {
  DataTableParamState,
  useSafeDataTableStateProps,
} from "@/common/hooks/useDataTableParamState";
import { createSortableColumnHelper } from "@/common/types/reactTableHelpers";
import { linkClickModifierHandler } from "@/common/utils/linkClickHandler";
import { shouldIgnoreRowNavigation } from "@/common/utils/rowNavigation";
import { AddColumnPopover } from "@/company/components/AddColumnPopover";
import { ExportUsersButton } from "@/company/components/ExportUsersButton";
import { SegmentFilters } from "@/company/components/SegmentFilters";
import { FeatureDisplay } from "@/feature/components/FeatureDisplay";
import { useFeatureNamesData } from "@/feature/data/useFeatureNamesData";
import UsersEmptyState from "@/user/components/UsersEmptyState";
import {
  AttributeType,
  userSystemAttributeFieldKeys,
} from "@reflag/shared/attributeFilter";
import type { FeatureName } from "@reflag/shared/featureAPI";
import { type FeatureMetric, UIFilter } from "@reflag/shared/filter";
import { SegmentDTO } from "@reflag/shared/segmentAPI";
import { UserUrl } from "@reflag/shared/urls";
import {
  UserDTO,
  UsersColumn,
  UsersColumnState,
  UserPage,
  UsersSortByColumn,
} from "@reflag/shared/userAPI";

type Props = {
  segment: SegmentDTO;
  tableState: DataTableParamState<UsersSortByColumn, UsersSortByColumn>;
  filter: UIFilter;
  onFilterChange: (filter: UIFilter | undefined) => void;
  data?: UserPage;
  totalCount?: number;
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  toolbarRightActions?: React.ReactNode;
};

export const defaultUserColumns: UsersSortByColumn[] = [
  "name",
  "firstSeen",
  "lastSeen",
  "email",
];

const MetricHeader = ({
  feature,
  metric,
}: {
  feature: FeatureName;
  metric: string;
}) => {
  const metricOption = featureMetricOptions.find(
    (option) => option.metric === metric,
  );
  return (
    <Tooltip
      label={`${feature.name} • ${metricOption?.label}`}
      openDelay={300}
      hasArrow
    >
      <HStack spacing={1.5}>
        <FeatureDisplay
          feature={feature}
          flexShrink={1}
          iconSize="xs"
          size="sm"
        />
        <Box color="dimmed">{metricOption?.icon}</Box>
      </HStack>
    </Tooltip>
  );
};

const featureMetricColumnTypes = {
  firstUsed: "date",
  lastUsed: "date",
  firstExposureAt: "date",
  lastExposureAt: "date",
  exposureCount: "number",
  usageCount: "number",
} as const satisfies Partial<Record<FeatureMetric, "date" | "number">>;

const supportedUserFeatureMetrics = Object.keys(
  featureMetricColumnTypes,
) as FeatureMetric[];

function isSupportedUserFeatureMetric(
  metric: string | undefined,
): metric is keyof typeof featureMetricColumnTypes {
  return !!metric && metric in featureMetricColumnTypes;
}

export const UsersTable = ({
  segment,
  data,
  isLoading,
  isFetching,
  isError,
  onRetry,
  tableState,
  filter,
  onFilterChange,
  toolbarRightActions,
  totalCount,
}: Props) => {
  const { env } = useEnvironment();
  const navigate = useNavigate();
  // We don't want to pass along invalid props to the data table
  const safeTableProps = useSafeDataTableStateProps(tableState);

  const columnHelper = createSortableColumnHelper<UserDTO, UsersSortByColumn>();
  const { data: features = [] } = useFeatureNamesData();
  const columns = useMemo(() => {
    return [
      columnHelper.accessor("name", {
        header: "Name",
        cell: ({ row }) => <UserDisplay user={row.original} />,
      }),
      columnHelper.accessor("id", {
        header: "ID",
        cell: (cell) => <Text fontSize="sm">{cell.getValue()}</Text>,
      }),
      columnHelper.accessor("firstSeen", {
        header: "First seen",
        sortDescFirst: true,
        cell: (cell) => {
          const value = cell.getValue();

          if (value) {
            return <TimestampCell value={value} />;
          }

          return <NotAvailableCell />;
        },
      }),
      columnHelper.accessor("lastSeen", {
        header: "Last seen",
        sortDescFirst: true,
        cell: (cell) => {
          const value = cell.getValue();

          if (value) {
            return <TimestampCell value={value} />;
          }

          return <NotAvailableCell />;
        },
      }),
      columnHelper.accessor("eventCount", {
        header: "Event count",
        cell: (cell) => <CompactNumber fontSize="sm" value={cell.getValue()} />,
      }),
      columnHelper.accessor("email", {
        header: "Email",
        cell: (cell) => {
          const value = cell.getValue();

          if (value) {
            return (
              <Link fontSize="sm" href={`mailto:${value}`}>
                {value}
              </Link>
            );
          }

          return <NotAvailableCell />;
        },
      }),
      ...tableState.columnStates.flatMap<ColumnDef<UserDTO, any>>(
        (columnState) => {
          const [key, id, metric] = columnState.id.split(".");
          if (key !== "featureMetrics") return [];
          const feature = features?.find((f) => f.id === id);
          if (!feature || !isSupportedUserFeatureMetric(metric)) return [];

          const cellType = featureMetricColumnTypes[metric];

          return [
            columnHelper.accessor(`featureMetrics.${id}.${metric}` as any, {
              id: `featureMetrics.${id}.${metric}`,
              sortDescFirst: cellType === "date",
              maxSize: 200,
              header: () => <MetricHeader feature={feature} metric={metric} />,
              meta: {
                isRemovable: true,
              },
              cell: (cell) => (
                <DataCell type={cellType} value={cell.getValue()} />
              ),
              enableSorting: false,
            }),
          ];
        },
      ),
      ...tableState.columnStates.flatMap((columnState) => {
        if (!columnState.id.startsWith("attributes.")) return [];
        const id = columnState.id.replace("attributes.", "");
        return [
          columnHelper.accessor(`attributes.${id}`, {
            id: `attributes.${id}`,
            maxSize: 200,
            header: () => (
              <Tooltip label={id} openDelay={300} hasArrow>
                <HStack spacing={1}>
                  <Box color="dimmed">
                    <RiBracesLine size={12} />
                  </Box>
                  <Text isTruncated>{id}</Text>
                </HStack>
              </Tooltip>
            ),
            meta: {
              isRemovable: true,
            },
            cell: (cell) => (
              <DataCell
                type={
                  (data?.metadata.attributeTypes[id] ??
                    ("string" as const)) as AttributeType
                }
                value={cell.getValue()}
              />
            ),
          }),
        ];
      }),
    ];
  }, [
    columnHelper,
    tableState.columnStates,
    features,
    data?.metadata.attributeTypes,
  ]);

  // when adding a new filter on the users table, add a column in the table when appropriate
  const onFilterChangeWrapper: FilterGroupOnChange = useCallback(
    (filterGroup, filterAction) => {
      if (filterAction.action === "create") {
        let columnId: UsersColumn | undefined;
        if (
          filterAction.filter.type === "userAttribute" &&
          !userSystemAttributeFieldKeys.includes(filterAction.filter.field)
        ) {
          columnId =
            `attributes.${filterAction.filter.field}` satisfies UsersColumn;
        } else if (
          filterAction.filter.type === "featureMetric" &&
          isSupportedUserFeatureMetric(filterAction.filter.metric)
        ) {
          columnId =
            `featureMetrics.${filterAction.filter.featureId}.${filterAction.filter.metric}` as UsersColumn;
        }

        // when adding a column in the filter, add it also to the table
        if (
          columnId &&
          tableState.columnStates.every(({ id }) => id !== columnId)
        ) {
          tableState.onColumnStatesChange([
            ...tableState.columnStates,
            {
              id: columnId,
              shown: true,
            },
          ]);
        }
      }

      onFilterChange(filterGroup);
    },
    [onFilterChange, tableState],
  );

  const users = data?.data || [];

  const isFiltered = filter !== undefined;

  return (
    <DataTable
      _toolbarLeft={{ wrap: "wrap", alignSelf: "flex-start" }}
      _toolbarRight={{ wrap: "wrap", flex: "1 1 min-content" }}
      columnCustomizerFooter={
        <Box p={2}>
          <AddColumnPopover<UsersColumnState>
            allowedFeatureMetrics={supportedUserFeatureMetrics}
            allowedTypes={["userAttribute", "featureMetric"]}
            columnStates={tableState.columnStates}
            setColumnStates={tableState.onColumnStatesChange}
          />
        </Box>
      }
      columns={columns}
      data={users}
      defaultColumns={defaultUserColumns}
      emptyState={
        <UsersEmptyState
          isError={isError}
          isFiltered={isFiltered}
          isLoading={isLoading}
          segment={segment}
          onRetry={onRetry}
        />
      }
      isFetching={isFetching}
      meta={data?.metadata}
      pageSize={20}
      searchPlaceholder="Search by name, ID, or email"
      tableId="users"
      toolbarLeftActions={
        <SegmentFilters
          columnStates={tableState.columnStates}
          filter={filter}
          onFilterChange={onFilterChangeWrapper}
        />
      }
      toolbarRightActions={
        <>
          {toolbarRightActions}
          <ExportUsersButton
            exportParams={{
              idNameEmailFilter: tableState.searchQuery,
              filter,
              sortBy: tableState.sorting[0]?.id || "name",
              sortOrder: tableState.sorting[0]?.desc ? "desc" : "asc",
              columns: tableState.columnStates.map((state) => state.id),
            }}
          />
        </>
      }
      totalCount={totalCount}
      variant="clickable"
      canCustomize
      canPaginate
      canSearch
      canSort
      onRowClick={(row, e) => {
        if (shouldIgnoreRowNavigation(e.target)) {
          return;
        }
        const url = UserUrl(env, row.original.id);
        e.preventDefault();
        linkClickModifierHandler(url, () => {
          navigate(url);
        })(e);
      }}
      {...safeTableProps}
    />
  );
};
